'use client';

import Image from 'next/image';
import NextLink from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Switch } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AnimatePresence, motion } from 'motion/react';
import { FiMoreVertical } from 'react-icons/fi';
import { TbMoon } from 'react-icons/tb';
import { useColorModeContext } from '@/components/providers';
import clsx from 'clsx';
import bottomGlowEllipsis from './assets/bottom_glow_ellipsis.svg';
import topLeftBackground from './assets/top_left_bg.svg';
import topRightBackground from './assets/top_right_bg.svg';
const slideVariants = {
  enter: direction => ({
    x: direction > 0 ? 48 : -48,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: direction => ({
    x: direction > 0 ? -48 : 48,
    opacity: 0
  })
};
function AuthSplitMenu() {
  const {
    colorMode,
    setColorMode
  } = useColorModeContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return <>
            <IconButton aria-label="More options" aria-haspopup="true" aria-expanded={open ? 'true' : undefined} onClick={e => setAnchorEl(e.currentTarget)} size="small" sx={{
      color: 'var(--foreground-muted)'
    }}>
                <FiMoreVertical className="text-lg" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} slotProps={{
      paper: {
        className: 'min-w-48! rounded-xl! border border-border! bg-raised! shadow-sm!'
      }
    }}>
                <MenuItem className="mx-1! rounded-lg! px-3! py-2.5!" onClick={() => setColorMode(colorMode === 'dark' ? 'light' : 'dark')}>
                    <ListItemIcon className="min-w-9!">
                        <TbMoon className="size-5" />
                    </ListItemIcon>
                    <ListItemText primary="Dark mode" primaryTypographyProps={{
          className: 'text-sm!'
        }} />
                    <Switch edge="end" checked={colorMode === 'dark'} onClick={e => e.stopPropagation()} onChange={(_, checked) => setColorMode(checked ? 'dark' : 'light')} />
                </MenuItem>
            </Menu>
        </>;
}
export function AuthSplitShell({
  children,
  footer,
  carouselItems,
  carouselItemRender,
  getCarouselItemKey,
  contentClassName,
  cardClassName,
  autoAdvanceMs = 5000
}) {
  const {
    isDark
  } = useColorModeContext();
  const [[slideIndex, direction], setSlidePage] = useState([0, 0]);
  const paginate = useCallback(newDirection => {
    setSlidePage(([index]) => {
      const next = (index + newDirection + carouselItems.length) % carouselItems.length;
      return [next, newDirection];
    });
  }, [carouselItems.length]);

  // Restart the auto-advance timer whenever the slide changes (manual or auto).
  useEffect(() => {
    if (carouselItems.length <= 1 || autoAdvanceMs <= 0) return;
    const interval = setInterval(() => {
      paginate(1);
    }, autoAdvanceMs);
    return () => clearInterval(interval);
  }, [paginate, slideIndex, carouselItems.length, autoAdvanceMs]);
  const item = carouselItems[slideIndex];
  return <div className="flex h-dvh w-full flex-col overflow-y-auto bg-muted px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
            <div className={clsx(`mx-auto my-auto flex w-full max-w-300 shrink-0 overflow-hidden rounded-[28px] border border-border bg-page shadow-sm md:min-h-[min(100%,40rem)] lg:min-h-[min(100%,45rem)] xl:min-h-[min(100%,50rem)] 2xl:min-h-[min(100%,55rem)]`, cardClassName ? ` ${cardClassName}` : '')}>
                <div className="relative flex w-full flex-col px-6 py-8 sm:px-10 sm:py-10 md:w-[48%] md:px-12 md:py-12 lg:px-14">
                    <div className="relative flex shrink-0 items-center justify-center">
                        <NextLink href="/" className="block">
                            <Image src={isDark ? '/images/intalent_logo_text_white.png' : '/images/intalent_logo_text_black.png'} alt="Intalent.ai" width={160} height={28} className="h-7 w-auto object-contain" />
                        </NextLink>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2">
                            <AuthSplitMenu />
                        </div>
                    </div>

                    <div className={`relative mx-auto flex w-full max-w-[420px] flex-1 flex-col justify-center py-10${contentClassName ? ` ${contentClassName}` : ''}`}>
                        {children}
                    </div>

                    {footer}
                </div>

                <div className="relative hidden min-h-[560px] flex-1 overflow-hidden rounded-r-[28px] bg-linear-to-b from-[#0d0919] to-[#0e0a1c] md:flex">
                    {/* Faint grid */}
                    <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='27' height='27' viewBox='0 0 27 27' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 0h27v27H0z'/%3E%3C/g%3E%3C/svg%3E")`
        }} />
                    {/* Top glows, faded out towards the middle */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 grid grid-cols-2 mask-b-from-60% mask-b-to-100%">
                        <Image src={topLeftBackground} alt="" className="h-auto w-full" />
                        <Image src={topRightBackground} alt="" className="h-auto w-full rotate-180" />
                    </div>
                    {/* Bottom underglow */}
                    <Image src={bottomGlowEllipsis} alt="" className="pointer-events-none absolute -bottom-5 left-0 h-auto w-full" />

                    <div className="relative z-10 flex flex-1 flex-col p-8 lg:p-10">
                        {/* Centered vertically so the slide sits level with the form's heading block on the left. */}
                        <div className="flex flex-1 items-center justify-center overflow-hidden">
                            <AnimatePresence initial={false} custom={direction} mode="wait">
                                <motion.div key={getCarouselItemKey?.(item, slideIndex) ?? slideIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35,
                ease: [0.4, 0, 0.2, 1]
              }} className="flex w-full flex-col items-center text-center">
                                    {carouselItemRender(item, slideIndex)}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <div className="mt-8 flex items-center justify-between gap-6">
                            <div className="flex items-center gap-1.5" aria-hidden>
                                {carouselItems.map((carouselItem, i) => <span key={getCarouselItemKey?.(carouselItem, i) ?? i} className={`h-1.5 rounded-full transition-all duration-300 ${i === slideIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/40'}`} />)}
                            </div>

                            <div className="flex shrink-0 items-center gap-2.5">
                                <IconButton aria-label="Previous slide" onClick={() => paginate(-1)} sx={{
                width: 44,
                height: 44,
                color: 'white',
                border: '2px solid rgba(255,255,255,0.55)',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)'
                }
              }}>
                                    <ChevronLeftIcon />
                                </IconButton>
                                <IconButton aria-label="Next slide" onClick={() => paginate(1)} sx={{
                width: 44,
                height: 44,
                color: 'white',
                border: '2px solid rgba(255,255,255,0.55)',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)'
                }
              }}>
                                    <ChevronRightIcon />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>;
}